import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Button from '@components/Button';
import CloseOutlineIcon from '@assets/images/icons/close-outlined.svg';
import Image from 'next/image';
import { useRouter } from 'next/router';
import axios from 'axios';
import { useTranslation } from 'next-i18next';
import { PetType } from '@prisma/client';

export const useUserExistedModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const router = useRouter();
  const { t: td } = useTranslation('dashboard');

  const checkEmailExists = async (email: string) => {
    try {
      const res = await axios.get(`/api/users/check?email=${email}`);

      if (res.data.user) {
        return true;
      }
      return false;
    } catch (err: any) {
      return null;
    }
  };

  const triggerCheck = async (email: string) => {
    const emailExists = await checkEmailExists(email);
    if (emailExists) {
      openModal();
      return true;
    }
    return false;
  };

  const sendSlackMessage = async (
    id: string,
    pet: {
      petName: string;
      petType: PetType;
      petBreed: string;
      petDateOfBirth: Date;
    },
    isTakaful: boolean
  ) => {
    await axios.post(`/api/slack/existing-eligibility-email`, {
      id,
      pet,
      isTakaful,
    });
  };

  const Modal = () => {
    if (!isOpen) return <></>;

    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 text-center py-auto">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[0.625rem] bg-white p-6 text-left align-middle transition-all">
                  <div className="flex justify-between items-center w-100">
                    <Dialog.Title
                      as="div"
                      className="flex flex-col items-start space-y-4"
                    >
                      <h3 className="text-xl font-semibold leading-5 text-gray-900 text-center">
                        {td('email_exists')}
                      </h3>
                    </Dialog.Title>
                    <button
                      onClick={closeModal}
                      className="w-6 h-6 flex items-center justify-center ml-auto mb-4"
                    >
                      <Image
                        src={CloseOutlineIcon}
                        height={18}
                        width={18}
                        alt="close-outlined-icon"
                      />
                    </button>
                  </div>

                  <div className="mt-4 space-y-4 text-start">
                    <p className="text-base text-black-800 font-semibold">
                      {td('email_exists_title')}
                    </p>
                  </div>

                  <div className="mt-4 space-y-4 text-start">
                    <p className="text-base text-gray-800 font-normal">
                      {td('email_exists_desc')}
                    </p>
                  </div>

                  <div className="mt-4">
                    <div className="flex flex-row items-center justify-center mt-4 w-full">
                      <Button
                        className="w-full"
                        onClick={() => {
                          router.push('/dashboard/auth/sign_in');
                        }}
                      >
                        {td('email_exists_cta')}
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  };

  return {
    isOpen,
    openModal,
    closeModal,
    triggerCheck,
    Modal,
    sendSlackMessage,
  };
};
